package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.PersonAdd
import androidx.compose.material.icons.filled.PersonRemove
import androidx.compose.material.icons.filled.SupervisorAccount
import androidx.compose.material.icons.filled.Storefront
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.GroupManager
import com.evoapps.evoshift.domain.model.GroupManagers
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.ui.util.label
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.model.WorkplaceGroup
import com.evoapps.evoshift.domain.model.WorkplaceGroups
import com.evoapps.evoshift.ui.adaptive.WideContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.WorkplaceGroupsEvent
import com.evoapps.evoshift.viewmodels.WorkplaceGroupsViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.SurfaceCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.edit
import evoshift.composeapp.generated.resources.group_managers_add
import evoshift.composeapp.generated.resources.group_managers_hint
import evoshift.composeapp.generated.resources.group_managers_level
import evoshift.composeapp.generated.resources.group_managers_no_candidates
import evoshift.composeapp.generated.resources.group_managers_none
import evoshift.composeapp.generated.resources.group_managers_person
import evoshift.composeapp.generated.resources.group_managers_remove
import evoshift.composeapp.generated.resources.group_managers_remove_confirm
import evoshift.composeapp.generated.resources.group_managers_title
import evoshift.composeapp.generated.resources.group_settings_open
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.workplace_groups_add
import evoshift.composeapp.generated.resources.workplace_groups_default_name
import evoshift.composeapp.generated.resources.workplace_groups_delete_confirm
import evoshift.composeapp.generated.resources.workplace_groups_empty_group
import evoshift.composeapp.generated.resources.workplace_groups_error_name
import evoshift.composeapp.generated.resources.workplace_groups_error_name_long
import evoshift.composeapp.generated.resources.workplace_groups_intro
import evoshift.composeapp.generated.resources.workplace_groups_manage
import evoshift.composeapp.generated.resources.workplace_groups_name
import evoshift.composeapp.generated.resources.workplace_groups_name_hint
import evoshift.composeapp.generated.resources.workplace_groups_new_title
import evoshift.composeapp.generated.resources.workplace_groups_no_group
import evoshift.composeapp.generated.resources.workplace_groups_profile_intro
import evoshift.composeapp.generated.resources.workplace_groups_rename_title
import evoshift.composeapp.generated.resources.workplace_groups_title
import evoshift.composeapp.generated.resources.workplace_groups_ungrouped
import evoshift.composeapp.generated.resources.workplace_groups_ungrouped_hint
import org.jetbrains.compose.resources.stringResource

/*
 * SH-267: "Grupy lokali", from the owner's profile. A card per group with its workplaces, each
 * with a choice of group (or none); "Poza grupami" lists the workplaces in no group. Every change
 * is saved at once (the whole grouping, optimistically). The default group, while it has no name
 * of its own, reads "Wszystkie lokale".
 */

/** The choice "No group" (no group id looks like it: ids never hold "~"). */
private const val NO_GROUP = "~none"

/** SH-269: test tag of a group's "Group settings" button (by group id). */
fun groupSettingsButtonTag(groupId: String) = "group_settings_button_$groupId"

/** SH-267: test tag of a workplace's group choice (by workplace id). */
fun workplaceGroupChoiceTag(workplaceId: String) = "workplace_group_choice_$workplaceId"

/** A group's name as shown: the default one without a name of its own reads "All workplaces". */
@Composable
fun WorkplaceGroup.displayName(): String =
    if (usesDefaultName) stringResource(Res.string.workplace_groups_default_name) else name

@Composable
fun WorkplaceGroupsScreen(
    viewModel: WorkplaceGroupsViewModel,
    onNavigateBack: () -> Unit,
    /** SH-269: opens a group's settings (null: not offered). */
    onOpenSettings: ((groupId: String) -> Unit)? = null
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    // null: no dialog; a blank id: a new group.
    var naming by remember { mutableStateOf<WorkplaceGroup?>(null) }
    var toDelete by remember { mutableStateOf<WorkplaceGroup?>(null) }
    // SH-268: the group a manager is being added to; a manager about to be removed (group, uid).
    var addingManagerTo by remember { mutableStateOf<WorkplaceGroup?>(null) }
    var managerToRemove by remember { mutableStateOf<Pair<WorkplaceGroup, String>?>(null) }
    val people = state.people

    val errorText = state.error?.asString()
    val successText = state.successMessage?.asString()
    LaunchedEffect(errorText, successText) {
        (errorText ?: successText)?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.onEvent(WorkplaceGroupsEvent.MessageShown)
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.workplace_groups_title), onBack = onNavigateBack) },
        floatingActionButton = {
            if (state.workplaces.isNotEmpty() && state.groups.size < WorkplaceGroups.MAX_GROUPS) {
                FloatingActionButton(onClick = { if (!state.isSaving) naming = WorkplaceGroup("", state.ownerId, "", emptyList()) }) {
                    Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.workplace_groups_add))
                }
            }
        }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().contentWidth(WideContentMaxWidth)) {
            Column(
                Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    stringResource(Res.string.workplace_groups_intro),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                state.groups.forEach { group ->
                    GroupCard(
                        group = group,
                        workplaces = state.workplaces,
                        groups = state.groups,
                        enabled = !state.isSaving,
                        onRename = { naming = group },
                        onDelete = { toDelete = group },
                        onMove = { wp, to -> viewModel.onEvent(WorkplaceGroupsEvent.Move(wp, to)) },
                        managers = if (viewModel.offersManagers && group.id.isNotBlank()) {
                            {
                                GroupManagersSection(
                                    group = group,
                                    people = people,
                                    enabled = !state.isSaving,
                                    onAdd = { addingManagerTo = group },
                                    onLevel = { uid, level -> viewModel.onEvent(WorkplaceGroupsEvent.SetManager(group.id, uid, level)) },
                                    onRemove = { uid -> managerToRemove = group to uid }
                                )
                            }
                        } else null,
                        onOpenSettings = onOpenSettings?.let { open -> { open(group.id) } }
                    )
                }
                val ungrouped = state.ungrouped
                if (ungrouped.isNotEmpty() && !state.isLoading) {
                    SurfaceCard(Modifier.fillMaxWidth()) {
                        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(stringResource(Res.string.workplace_groups_ungrouped), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                            Text(stringResource(Res.string.workplace_groups_ungrouped_hint), style = MaterialTheme.typography.bodySmall)
                            ungrouped.forEach { wp ->
                                GroupChoice(wp, state.workplaces[wp] ?: wp, null, state.groups, !state.isSaving) { to ->
                                    viewModel.onEvent(WorkplaceGroupsEvent.Move(wp, to))
                                }
                            }
                        }
                    }
                }
                Spacer(Modifier.height(80.dp))
            }
            LoadingOverlay(isLoading = state.isLoading)
        }
    }

    naming?.let { group ->
        GroupNameDialog(
            initial = group,
            onDismiss = { naming = null },
            onSave = { name ->
                viewModel.onEvent(if (group.id.isBlank()) WorkplaceGroupsEvent.AddGroup(name) else WorkplaceGroupsEvent.Rename(group.id, name))
                naming = null
            }
        )
    }

    toDelete?.let { group ->
        EvoConfirmDialog(
            title = stringResource(Res.string.delete),
            text = stringResource(Res.string.workplace_groups_delete_confirm, group.displayName()),
            confirmLabel = stringResource(Res.string.delete),
            onConfirm = {
                viewModel.onEvent(WorkplaceGroupsEvent.Delete(group.id))
                toDelete = null
            },
            onDismiss = { toDelete = null },
            destructive = true,
            icon = Icons.Default.Delete
        )
    }

    addingManagerTo?.let { group ->
        AddGroupManagerDialog(
            candidates = state.candidatesFor(group),
            onDismiss = { addingManagerTo = null },
            onAdd = { uid, level ->
                viewModel.onEvent(WorkplaceGroupsEvent.SetManager(group.id, uid, level))
                addingManagerTo = null
            }
        )
    }

    managerToRemove?.let { (group, uid) ->
        EvoConfirmDialog(
            title = stringResource(Res.string.group_managers_title),
            text = stringResource(Res.string.group_managers_remove_confirm, people[uid]?.displayName ?: uid, group.displayName()),
            confirmLabel = stringResource(Res.string.delete),
            onConfirm = {
                viewModel.onEvent(WorkplaceGroupsEvent.RemoveManager(group.id, uid))
                managerToRemove = null
            },
            onDismiss = { managerToRemove = null },
            destructive = true,
            icon = Icons.Default.PersonRemove
        )
    }
}

/**
 * SH-268: a group's managers ("Kierownicy grupy"): each with their level (a change is saved at
 * once) and a remove button; "Add group manager" picks one of the people of its workplaces.
 */
@Composable
private fun GroupManagersSection(
    group: WorkplaceGroup,
    people: Map<String, User>,
    enabled: Boolean,
    onAdd: () -> Unit,
    onLevel: (userId: String, level: UserPermission) -> Unit,
    onRemove: (userId: String) -> Unit
) {
    Column(Modifier.fillMaxWidth().padding(top = 8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.SupervisorAccount, contentDescription = null, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(8.dp))
            Text(stringResource(Res.string.group_managers_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
        }
        Text(
            stringResource(Res.string.group_managers_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        if (group.managers.isEmpty()) {
            Text(stringResource(Res.string.group_managers_none), style = MaterialTheme.typography.bodySmall)
        }
        group.managers.forEach { manager ->
            ManagerRow(manager, people[manager.userId]?.displayName ?: manager.userId, enabled, onLevel, onRemove)
        }
        if (group.managers.size < GroupManagers.MAX_MANAGERS) {
            TextButton(onClick = onAdd, enabled = enabled, modifier = Modifier.align(Alignment.End)) {
                Icon(Icons.Default.PersonAdd, contentDescription = null)
                Spacer(Modifier.width(4.dp))
                Text(stringResource(Res.string.group_managers_add))
            }
        }
    }
}

@Composable
private fun ManagerRow(
    manager: GroupManager,
    name: String,
    enabled: Boolean,
    onLevel: (userId: String, level: UserPermission) -> Unit,
    onRemove: (userId: String) -> Unit
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        // The person's name over their level: the choice changes it at once.
        EvoDropdown(
            label = name,
            options = GroupManagers.LEVELS,
            selected = manager.permission,
            onSelected = { if (it != manager.permission) onLevel(manager.userId, it) },
            optionLabel = { it.label() },
            modifier = Modifier.weight(1f).testTag(groupManagerLevelTag(manager.userId)),
            enabled = enabled,
            onLight = true
        )
        IconButton(onClick = { onRemove(manager.userId) }, enabled = enabled) {
            Icon(
                Icons.Default.PersonRemove,
                contentDescription = stringResource(Res.string.group_managers_remove, name),
                tint = MaterialTheme.colorScheme.error
            )
        }
    }
}

/** SH-268: test tag of a group manager's level choice (by uid). */
fun groupManagerLevelTag(userId: String) = "group_manager_level_$userId"

/** SH-268: a new group manager: one of the people of the group's workplaces, and their level. */
@Composable
private fun AddGroupManagerDialog(
    candidates: List<User>,
    onDismiss: () -> Unit,
    onAdd: (userId: String, level: UserPermission) -> Unit
) {
    var person by remember { mutableStateOf<String?>(null) }
    var level by remember { mutableStateOf(UserPermission.MANAGER) }
    EvoDialog(
        title = stringResource(Res.string.group_managers_add),
        onDismiss = onDismiss,
        icon = Icons.Default.PersonAdd,
        confirm = if (candidates.isEmpty()) null else DialogAction(stringResource(Res.string.save), {
            person?.let { onAdd(it, level) }
        }, enabled = person != null)
    ) {
        if (candidates.isEmpty()) {
            Text(stringResource(Res.string.group_managers_no_candidates), style = MaterialTheme.typography.bodyMedium)
        } else {
            val names = candidates.associate { it.uid to it.displayName }
            EvoDropdown(
                label = stringResource(Res.string.group_managers_person),
                options = candidates.map { it.uid },
                selected = person,
                onSelected = { person = it },
                optionLabel = { names[it].orEmpty() },
                modifier = Modifier.fillMaxWidth()
            )
            EvoDropdown(
                label = stringResource(Res.string.group_managers_level),
                options = GroupManagers.LEVELS,
                selected = level,
                onSelected = { level = it },
                optionLabel = { it.label() },
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun GroupCard(
    group: WorkplaceGroup,
    workplaces: Map<String, String>,
    groups: List<WorkplaceGroup>,
    enabled: Boolean,
    onRename: () -> Unit,
    onDelete: () -> Unit,
    onMove: (workplaceId: String, toGroupId: String?) -> Unit,
    /** SH-268: the group's managers' section; null: not offered. */
    managers: (@Composable () -> Unit)? = null,
    onOpenSettings: (() -> Unit)? = null
) {
    SurfaceCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(group.displayName(), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                IconButton(onClick = onRename, enabled = enabled) {
                    Icon(Icons.Default.Edit, contentDescription = stringResource(Res.string.edit), modifier = Modifier.size(20.dp))
                }
                IconButton(onClick = onDelete, enabled = enabled) {
                    Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.delete), modifier = Modifier.size(20.dp), tint = MaterialTheme.colorScheme.error)
                }
            }
            val members = group.workplaceIds.filter { it in workplaces }
            if (members.isEmpty()) {
                Text(stringResource(Res.string.workplace_groups_empty_group), style = MaterialTheme.typography.bodySmall)
            }
            members.forEach { wp ->
                GroupChoice(wp, workplaces.getValue(wp), group.id, groups, enabled) { to -> onMove(wp, to) }
            }
            managers?.invoke()
            // SH-269: the group's settings, which its workplaces follow by section.
            if (onOpenSettings != null && members.isNotEmpty()) {
                TextButton(onClick = onOpenSettings, enabled = enabled, modifier = Modifier.align(Alignment.End).testTag(groupSettingsButtonTag(group.id))) {
                    Text(stringResource(Res.string.group_settings_open))
                }
            }
        }
    }
}

/** A workplace with the group it is in: a choice of the owner's groups or none. */
@Composable
private fun GroupChoice(
    workplaceId: String,
    workplaceName: String,
    groupId: String?,
    groups: List<WorkplaceGroup>,
    enabled: Boolean,
    onChoose: (String?) -> Unit
) {
    // A group's id, or NO_GROUP (shown as "No group", not as an empty field).
    val options: List<String> = groups.map { it.id } + NO_GROUP
    EvoDropdown(
        label = workplaceName,
        options = options,
        selected = groupId?.takeIf { id -> groups.any { it.id == id } } ?: NO_GROUP,
        onSelected = { onChoose(it.takeIf { id -> id != NO_GROUP }) },
        optionLabel = { id ->
            groups.firstOrNull { it.id == id }?.displayName() ?: stringResource(Res.string.workplace_groups_no_group)
        },
        modifier = Modifier.fillMaxWidth().testTag(workplaceGroupChoiceTag(workplaceId)),
        enabled = enabled,
        onLight = true
    )
}

@Composable
private fun GroupNameDialog(initial: WorkplaceGroup, onDismiss: () -> Unit, onSave: (String) -> Unit) {
    var name by remember { mutableStateOf(initial.name) }
    var tried by remember { mutableStateOf(false) }
    val trimmed = name.trim()
    val problem = when {
        trimmed.isEmpty() && !initial.isDefault -> stringResource(Res.string.workplace_groups_error_name)
        trimmed.length > WorkplaceGroups.MAX_NAME -> stringResource(Res.string.workplace_groups_error_name_long)
        else -> null
    }
    EvoDialog(
        title = stringResource(if (initial.id.isBlank()) Res.string.workplace_groups_new_title else Res.string.workplace_groups_rename_title),
        onDismiss = onDismiss,
        icon = Icons.Default.Storefront,
        confirm = DialogAction(stringResource(Res.string.save), {
            tried = true
            if (problem == null) onSave(trimmed)
        })
    ) {
        EvoTextField(
            value = name,
            onValueChange = { name = it },
            label = stringResource(Res.string.workplace_groups_name),
            placeholder = if (initial.isDefault) stringResource(Res.string.workplace_groups_default_name) else stringResource(Res.string.workplace_groups_name_hint),
            error = problem?.takeIf { tried },
            modifier = Modifier.fillMaxWidth()
        )
    }
}

/** SH-267: the profile's section for the owner: what groups are for, and the way in. */
@Composable
fun WorkplaceGroupsProfileSection(onOpen: () -> Unit) {
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(stringResource(Res.string.workplace_groups_title), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
        Text(
            stringResource(Res.string.workplace_groups_profile_intro),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        TextButton(onClick = onOpen, modifier = Modifier.align(Alignment.End)) {
            Icon(Icons.Default.Storefront, contentDescription = null)
            Spacer(Modifier.width(4.dp))
            Text(stringResource(Res.string.workplace_groups_manage))
        }
    }
}
